import { UseEntityData } from "crqlar-react-common/dist/components/useEntity";
import { EliteConfig } from "crqlar-react-common/dist/models/integrations/elite/eliteConfig.model";
import { Alert, Divider, MenuItem, Select, Stack, Switch } from "@mui/material";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import OtaMappingRulesForm from "../../OtaMappingRulesForm";
import { ProcessingStrategy } from "crqlar-react-common/dist/models/integrations/baseIntegrationConfig.model";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
  eliteConfigData: UseEntityData<EliteConfig>;
}

const EliteConfigForm = ({ eliteConfigData }: Props) => {

  const { t } = useTranslation();


  const eliteConfig = eliteConfigData.item;
  const nameMissing = isRequiredTextMissing(eliteConfigData, eliteConfig.name);
  const setEliteConfig = eliteConfigData.setItem;

  //region Handlers

  const updateField = (field: keyof EliteConfig, value: any) => {
    setEliteConfig({ ...eliteConfig, [field]: value });
  };


  //endregion Handlers

  //region UI

  let credentialFields;
  if (eliteConfig.id) {
    credentialFields =
    <>
                <Property
        title={t('Username')}
        tooltip={t('Generated username for authenticating Elite import requests.')}>

                    <TextField
          id={'username'}
          size={'small'}
          value={eliteConfig.id || ''}
          fullWidth
          disabled />

                </Property>

                <Property
        title={t('Password / Auth token')}
        tooltip={t('Generated token used as the password for Elite import requests.')}>

                    <TextField
          id={'token'}
          size={'small'}
          value={eliteConfig.authorizationToken || ''}
          fullWidth
          disabled />

                </Property>

                <Property
        title={t('Integration instructions')}
        tooltip={t('Endpoint and authentication details for sending data into this connector.')}>

                    <Stack gap={1}>
                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            <Typography>
                                {t("Send")} <strong>{t("POST")}</strong> with XML body
                                to <strong>{process.env.REACT_APP_API_ENDPOINT}{t("/v3/integrations/elite/ota")}</strong>
                            </Typography>
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            <Typography>
                                {t("Use")} <strong>{t("Basic Auth")}</strong> {t("with provided username and password for authorization")}
                            </Typography>
                        </Alert>
                    </Stack>

                </Property>
            </>;

  } else {
    credentialFields =
    <Alert
      variant={'outlined'}
      severity={'info'}
      sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                {t('Please save the Elite configuration first to see the integration details')}
            </Alert>;

  }

  const credentialsSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Name')}
        tooltip={t('Internal label for identifying this Elite integration configuration.')}>

                    <TextField
          id={'name'}
          size={'small'}
          value={eliteConfig.name || ''}
          error={nameMissing}
          onChange={(e) => updateField('name', e.target.value)}
          fullWidth
          required />

                </Property>

                {credentialFields}
            </Stack>
        </Stack>;


  const configurationSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Enabled')}
        tooltip={t('Turns the Elite integration on or off.')}>

                    <Switch
          id={'enabled'}
          checked={eliteConfig.enabled}
          onChange={(e) => updateField('enabled', e.target.checked)} />

                </Property>

                <Property
        title={t('Processing strategy')}
        tooltip={t('Controls whether imported messages are processed immediately or queued.')}>

                    <Select
          value={eliteConfig.processingStrategy || ProcessingStrategy.IMMEDIATE}
          onChange={(e) => updateField('processingStrategy', e.target.value)}
          fullWidth
          size={'small'}
          variant={'outlined'}>

                        {
          Object.values(ProcessingStrategy).map((type, index) =>
          <MenuItem
            key={index}
            value={type}
            id={`processing-strategy-${type}`}>

                                    {t(`Processing strategy ${type}`)}
                                </MenuItem>
          )
          }
                    </Select>
                </Property>

                <Property
        title={t('Property ID / Name (optional, multiproperty)')}
        tooltip={t('Optional property identifier assigned to all data imported through this connector.')}>

                    <Stack
          width={'100%'}
          gap={1}>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'error'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
            id={'name'}
            size={'small'}
            value={eliteConfig.propertyId || ''}
            onChange={(e) => updateField('propertyId', e.target.value)}
            fullWidth
            required />

                    </Stack>

                </Property>

                <OtaMappingRulesForm value={eliteConfig.otaMappingRules} onChange={(r) => updateField('otaMappingRules', r)} />
            </Stack>
        </Stack>;


  //endregion UI

  return (
    <Stack
      sx={{
        width: '100%',
        gap: 6
      }}>

            {credentialsSection}
            <Divider />
            {configurationSection}
        </Stack>);


};

export default EliteConfigForm;
